import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  CircularProgress
} from '@mui/material';

import { config } from "../../config";
import { useEventsApi } from '../../hooks/useEventsApi';
import {
  NumberRangeBaseFunction,
  NumberRangeSubFunction,
  descriptionBodyText,
  numberRangeDisplayName,
} from '../../shared/NumberRange';

interface Props {
  baseId: number;
  data: {
    selectedSubFunction?: number;
  };
  onValidationChange: (valid: boolean) => void;
  onDataChange: (data: any) => void;
  onAutoContinue: () => void;
}

export default function Step2Form({
  baseId,
  data,
  onValidationChange,
  onDataChange,
  onAutoContinue,
}: Props) {

  const { callApi } = useEventsApi();
  const theme = useTheme();

  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(true);

  const [baseFunction, setBaseFunction] =
    useState<NumberRangeBaseFunction | null>(null);

  const [subFunctions, setSubFunctions] =
    useState<NumberRangeSubFunction[]>([]);

  const [selectedSubFunction, setSelectedSubFunction] =
    useState<number | null>(data.selectedSubFunction ?? null);

  const languageCode: "ger" | "eng" = "eng";

  const isNonEmpty = (s?: string | null) =>
    typeof s === "string" && s.trim().length > 0;

  // -------------------------------
  // Daten laden
  // -------------------------------
  const fetchBaseFunction = async () => {
    try {
      setLoadingBase(true);
      const url = `${config.apiUrl}/api/number-range-base-function/${baseId}/`;
      const res = await callApi(url, "GET");
      setBaseFunction(res);
    } catch (error) {
      console.error("Fehler beim Laden der BaseFunction:", error);
    } finally {
      setLoadingBase(false);
    }
  };

  const fetchSubFunctions = async () => {
    try {
      setLoadingSubs(true);
      const url =
        `${config.apiUrl}/api/number-range-sub-function/?base=${baseId}`;
      const res = await callApi(url, "GET");
      setSubFunctions(res.results);
    } catch (error) {
      console.error("Fehler beim Laden der SubFunctions:", error);
    } finally {
      setLoadingSubs(false);
    }
  };

  useEffect(() => {
    fetchBaseFunction();
    fetchSubFunctions();
  }, [baseId]);

  // -------------------------------
  // Labels
  // -------------------------------
  const getBaseLabel = (bf: NumberRangeBaseFunction | null) => {
    if (!bf) return "";
    return numberRangeDisplayName(bf.descriptions, languageCode);
  };

  const getSubLabel = (sf: NumberRangeSubFunction) => {
    return numberRangeDisplayName(sf.descriptions, languageCode);
  };

  // -------------------------------
  // SubFunctions filtern
  // -------------------------------
  const filteredSubFunctions = useMemo(() => {
    return subFunctions.filter((sf) => isNonEmpty(numberRangeDisplayName(sf.descriptions, languageCode)));
  }, [subFunctions, languageCode]);

  // -------------------------------
  // Loading
  // -------------------------------
  if (loadingBase || loadingSubs) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <Box sx={{ maxHeight: '80vh', overflowY: 'auto', p: 2 }}>
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          fontWeight: "bold",
          textAlign: "center",
          color: theme.palette.text.primary
        }}
      >
        Please select {getBaseLabel(baseFunction)} sub-function:
      </Typography>
      <Grid container spacing={2} justifyContent="center">
        {filteredSubFunctions.map((sf) => {

          const label = getSubLabel(sf);
          const subDescription = descriptionBodyText(sf.descriptions, languageCode);
          const isSelected = selectedSubFunction === sf.id;

          return (
            <Grid
              key={sf.id}
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={{
                display: "flex",
                flexGrow: 1,
                minWidth: 280,
              }}
            >
              <Card
                onClick={() => {

                  const id = sf.id;

                  // lokalen State setzen
                  setSelectedSubFunction(id);

                  // Parent sofort informieren
                  onValidationChange(true);
                  onDataChange({
                    selectedSubFunction: id,
                    selectedSubFunctionName: label,
                    selectedBaseFunctionName: getBaseLabel(baseFunction),
                    selectedSubDigit: sf.digit,
                  });

                  // direkt weiter
                  onAutoContinue();
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  cursor: "pointer",
                  border: isSelected
                    ? `3px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  boxShadow: isSelected
                    ? theme.shadows[8]
                    : theme.shadows[1],
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: theme.shadows[6],
                    transform: "scale(1.02)",
                  },
                  width: "100%",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" component="div" fontWeight={600}>
                    {label}
                  </Typography>
                  {subDescription ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                    >
                      {subDescription}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}